import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import { formatErr } from "./api";
import { Lock, Mail, AlertCircle, ShieldCheck } from "lucide-react";

const HERO_IMAGES = [
  "https://customer-assets.emergentagent.com/job_bananax-luxury/artifacts/z9fufo8p_image.png",
  "https://customer-assets.emergentagent.com/job_bananax-luxury/artifacts/5rsr7twr_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2003_11_22%20PM.png",
  "https://customer-assets.emergentagent.com/job_bananax-luxury/artifacts/0df0o8ax_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2003_50_35%20PM.png",
  "https://customer-assets.emergentagent.com/job_bananax-luxury/artifacts/wdis4w1e_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2003_48_17%20PM.png",
  "https://customer-assets.emergentagent.com/job_bananax-luxury/artifacts/oc5db2xs_ChatGPT%20Image%20Jun%2016%2C%202026%2C%2003_02_18%20PM.png",
];

export default function AdminLogin() {
  const { user, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  useEffect(() => {
    if (heroPaused) return undefined;
    const id = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(id);
  }, [heroPaused]);

  if (user) return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid lg:grid-cols-2" data-testid="admin-login-page">
      {/* LEFT — hero image (desktop only) */}
      <aside
        className="hidden lg:block relative overflow-hidden bg-ink-950 group"
        data-testid="admin-login-hero"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
      >
        {HERO_IMAGES.map((src, idx) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden={idx !== heroIdx}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1800ms] ease-in-out ${
              idx === heroIdx ? "opacity-100" : "opacity-0"
            }`}
            loading={idx === 0 ? "eager" : "lazy"}
            data-testid={`hero-slide-${idx}`}
          />
        ))}
        {/* Soft cinematic gradient overlay so copy stays readable */}
        <div className="absolute inset-0 bg-gradient-to-tr from-ink-950/85 via-ink-950/30 to-burgundy/20" />
        {/* Top hairline gold accent */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" />

        <div className="relative h-full flex flex-col justify-between p-14 text-bone">
          {/* Top — brand mark */}
          <div>
            <h1 className="font-serif text-4xl">
              Banana<span className="text-gold italic">X</span>
              <span className="text-gold text-base align-top">&trade;</span>
            </h1>
            <p className="label-eyebrow text-gold mt-2">Operations Console</p>
          </div>

          {/* Bottom — tagline + atelier line */}
          <div className="space-y-6 max-w-md">
            <p className="font-serif text-3xl leading-snug text-bone">
              Crafted for the&nbsp;crown.
              <span className="block text-gold/90 italic text-2xl mt-2">
                Operated with discretion.
              </span>
            </p>
            <div className="h-px w-16 bg-gold/40" />
            <p className="text-bone/70 font-light text-sm leading-relaxed">
              Every action inside this console — stock movements, partner payments, customer messages — is timestamped and audit-logged.
            </p>
            <p className="label-eyebrow text-bone/40 tracking-[0.18em]">
              BananaX Atelier · Dwarka, New Delhi · India
            </p>

            {/* Slide indicators */}
            <div className="flex items-center gap-2 pt-2" data-testid="hero-indicators">
              {HERO_IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setHeroIdx(idx)}
                  aria-label={`Show slide ${idx + 1} of ${HERO_IMAGES.length}`}
                  className={`h-[2px] transition-all duration-700 hover:bg-gold/80 ${
                    idx === heroIdx ? "w-10 bg-gold" : "w-5 bg-bone/25"
                  }`}
                  data-testid={`hero-indicator-${idx}`}
                />
              ))}
              <span
                className={`ml-3 label-eyebrow text-[10px] tracking-[0.22em] transition-opacity duration-500 ${
                  heroPaused ? "opacity-100 text-gold" : "opacity-0"
                }`}
                data-testid="hero-paused-label"
                aria-hidden={!heroPaused}
              >
                Paused
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* RIGHT — form */}
      <main
        className="flex items-center justify-center px-6 py-12 relative"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at top right, rgba(212,175,55,0.08), transparent 60%), radial-gradient(ellipse at bottom left, rgba(135,32,46,0.05), transparent 60%)",
        }}
      >
        {/* Mobile-only gold rule */}
        <div className="lg:hidden absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" />

        <div className="w-full max-w-md">
          {/* Mobile wordmark (the hero is hidden on small screens) */}
          <div className="lg:hidden text-center mb-10">
            <h1 className="font-serif text-4xl text-ink-950">
              Banana<span className="text-gold italic">X</span>
              <span className="text-gold text-base align-top">&trade;</span>
            </h1>
            <p className="label-eyebrow mt-2 text-ink-950/60">Operations Console</p>
          </div>

          <form
            onSubmit={onSubmit}
            className="bg-white border border-ink-950/10 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)] p-10 space-y-6"
            data-testid="admin-login-form"
          >
            <div className="flex items-start justify-between">
              <div>
                <h2 className="font-serif text-2xl text-ink-950">Sign in</h2>
                <p className="text-ink-950/60 text-sm font-light mt-1">
                  Authorised staff only.
                </p>
              </div>
              <div className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 text-[10px] label-eyebrow">
                <ShieldCheck className="w-3 h-3" /> Secure
              </div>
            </div>

            <label className="block">
              <span className="label-eyebrow text-ink-950/50 mb-2 inline-block">Email</span>
              <div className="flex items-center bg-white border border-ink-950/15 px-3 focus-within:border-gold focus-within:ring-1 focus-within:ring-gold/30 transition-all">
                <Mail className="w-4 h-4 text-ink-950/40 mr-2" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-transparent text-ink-950 py-3 outline-none text-sm font-light placeholder:text-ink-950/30"
                  placeholder="you@bananax.in"
                  data-testid="admin-email-input"
                />
              </div>
            </label>

            <label className="block">
              <span className="label-eyebrow text-ink-950/50 mb-2 inline-block">Password</span>
              <div className="flex items-center bg-white border border-ink-950/15 px-3 focus-within:border-gold focus-within:ring-1 focus-within:ring-gold/30 transition-all">
                <Lock className="w-4 h-4 text-ink-950/40 mr-2" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex-1 bg-transparent text-ink-950 py-3 outline-none text-sm font-light placeholder:text-ink-950/30"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                />
              </div>
            </label>

            {err && (
              <div className="flex items-start gap-2 text-burgundy bg-burgundy/5 border border-burgundy/20 p-3 text-sm" data-testid="admin-login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span className="font-light">{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-ink-950 hover:bg-ink-950/90 text-bone tracking-[0.18em] text-xs uppercase py-3.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="admin-login-submit"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>

            <p className="text-ink-950/40 text-[10px] text-center font-light tracking-wider pt-1">
              All actions are logged · BananaX Atelier · Dwarka, New Delhi
            </p>
          </form>

          <p className="text-center label-eyebrow mt-8 text-ink-950/40">
            Be Ready. <span className="text-gold">Stay Protected.</span>
          </p>
        </div>
      </main>
    </div>
  );
}
